<template>
  <div class="c-sharing-settings-item">
    <div class="flex items-center gap-10">
      <BittsSvg
        :svg="
          dataShare.integrationType ? `${dataShare.integrationType}Icon` : ''
        "
        class="w-16 h-16 mr-2 min-w-[16px]"
      />
      <h3 class="font-bold">{{ dataShare.populationName }}</h3>
      <BittsSelect
        v-model="selectedSharing"
        class="c-sharing-settings-item__sharing"
        :class="{
          'c-sharing-settings-item__sharing--overlap-counts': isOverlapCounts,
        }"
        dropdown-class-name="c-sharing-settings-item__sharing--dropdown"
        :list-height="300"
        :dropdown-match-select-width="false"
        :options="sharingSelectOptions"
        :use-disabled-options="!isGreenfieldSettingEnabled"
        :disabled-option-content="{
          text: 'This sharing option was disabled. Please reach out to your admins.',
        }"
        :searchable="false"
        @change="
          (visibility: SharingType) =>
            handleChange({
              visibility,
              presetId: selectedPresetId ?? undefined,
            })
        "
        :disabled="disabled"
      >
        <template #option="{ option }">
          <div class="flex flex-col">
            <div
              class="c-sharing-settings-item__sharing-option-label"
              :class="{
                'text-warning-text': option.value === SHARING_TYPES.OLAPS_ONLY,
                'text-info-text': option.value !== SHARING_TYPES.OLAPS_ONLY,
              }"
            >
              {{ option.label }}
            </div>
            <div class="c-sharing-settings-item__sharing-option-description">
              {{ option.description }}
            </div>
          </div>
        </template>
      </BittsSelect>
    </div>
    <div class="flex items-center gap-16">
      <BittsSelect
        v-if="!isOverlapCounts"
        ref="selectRef"
        v-model="selectedPresetId"
        :has-opt-groups="true"
        class="c-sharing-settings-item__preset"
        dropdown-class-name="c-sharing-settings-item__preset-dropdown"
        :dropdown-match-select-width="false"
        :options="presetOptionsWithAction"
        :searchable="false"
        @change="
          (presetId: number) =>
            handleChange({ visibility: selectedSharing, presetId })
        "
        :disabled="disabled"
      >
        <template #suffix="{ option }">
          <BittsButton
            v-if="option.value !== 'custom' || selectedSingleSharePreset"
            class="c-sharing-settings-item__preset-details-btn"
            variant="ghost"
            type="neutral"
            size="small"
            :center-icon="getPresetSuffixIcon(option.value)"
            @click="(e: Event) => handleOpenPresetModal(e, option.value)"
          />
        </template>
      </BittsSelect>
      <SharingItemPartnerAvatars
        v-if="isFromDefaultSharing"
        :population-id="dataShare.populationId"
      />
      <template v-if="!disabled && isForPartner">
        <BittsTooltip>
          <template #title>
            {{
              isDefaultSettings
                ? 'This partner uses your default sharing settings for this Population.'
                : `You've customized sharing for this partner. Changes to your default settings won't apply here.`
            }}
          </template>
          <BittsTag type="neutral" size="small" class="min-w-[125px]">
            {{ isDefaultSettings ? 'Default Settings' : 'Custom Settings' }}
          </BittsTag>
        </BittsTooltip>
        <BittsTooltip>
          <template #title>
            {{
              hasDefaultSettings
                ? 'Restore Default Settings'
                : 'No Default Sharing Settings for this Population'
            }}
          </template>
          <BittsButton
            @click="handleRestoreToDefault"
            variant="outline"
            type="neutral"
            size="x-small"
            :center-icon="['fas', 'rotate-left']"
            :disabled="isDefaultSettings || !hasDefaultSettings"
          />
        </BittsTooltip>
      </template>
      <BittsTooltip v-if="!disabled">
        <template #title>Unshare Population</template>
        <BittsButton
          @click="handleChange({ visibility: SHARING_TYPES.HIDDEN })"
          variant="outline"
          type="neutral"
          size="x-small"
          class="p-6"
        >
          <template #icon>
            <FontAwesomeIcon
              :icon="['fak', 'x']"
              :style="{ height: '12px', width: '12px', color: 'currentColor' }"
              class="text-danger-accent"
            />
          </template>
        </BittsButton>
      </BittsTooltip>
      <BittsButton
        v-else
        class="c-sharing-settings-item__preset-details-btn"
        variant="ghost"
        type="neutral"
        size="small"
        :center-icon="getPresetSuffixIcon(Number(selectedPresetId))"
        @click="
          (e: Event) => handleOpenPresetModal(e, Number(selectedPresetId))
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsButton,
  BittsSelect,
  BittsSvg,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { storeToRefs } from 'pinia';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import {
  SHARING_TYPES,
  SharingType,
  sharingSelectOptions,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import { DataSharePreset } from '@/types/data_share_presets';

import {
  DataShareItem,
  PresetOptionsGroup,
} from '../composables/useSharingSettings';

import SharingItemPartnerAvatars from './SharingItemPartnerAvatars.vue';

const {
  dataShare,
  presetOptions,
  singleSharePresets,
  createDataShare,
  updateDataShare,
  resetDataShare,
  defaultSharing,
} = defineProps<{
  dataShare: DataShareItem;
  presetOptions?: PresetOptionsGroup[];
  singleSharePresets: DataSharePreset[];
  createDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  updateDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  resetDataShare: (params: DataShareItem) => void;
  defaultSharing?: DataShareItem;
}>();

const router = useRouter();
const route = useRoute();
const { hasPermission, isAdmin } = useAuth();

const selectRef = ref<InstanceType<typeof BittsSelect> | null>(null);
const isOpenFieldPresetsModal = ref(false);
const isGreenfieldSettingEnabled = ref(true);

const { isSupernodeTier } = storeToRefs(useBillingStore());

const hasSharingPermission = computed(() => hasPermission('manage:sharing'));
const hasPopulationsPermission = computed(() =>
  hasPermission('manage:populations'),
);

const selectedSharing = computed(() => dataShare.visibility);
const selectedPresetId = computed(() => dataShare.presetId);

const isOverlapCounts = computed(
  () => selectedSharing.value === SHARING_TYPES.OLAPS_ONLY,
);

const isFromInviteOnboarding = computed(() =>
  isRouteNameInMatched('onboarding_accept_proposal'),
);
const isFromSharingOnboarding = computed(() =>
  isRouteNameInMatched('onboarding_data_sharing'),
);
const isFromInvitePartners = computed(() =>
  isRouteNameInMatched('accept_proposal'),
);
const isFromPendingRequests = computed(() =>
  isRouteNameInMatched('pending_requests'),
);
const isFromDefaultSharing = computed(() =>
  isRouteNameInMatched('default-sharing'),
);
const isFromPartnerSettings = computed(() =>
  isRouteNameInMatched('partnership-settings'),
);
const isForPartner = computed(
  () =>
    isFromPartnerSettings.value ||
    isFromInvitePartners.value ||
    isFromPendingRequests.value ||
    isFromInviteOnboarding.value,
);

const disabled = computed(() =>
  isFromDefaultSharing.value
    ? !(hasPopulationsPermission.value && hasSharingPermission.value)
    : !hasSharingPermission.value,
);

const isDefaultSettings = computed(() => dataShare.partnerId === null);
const hasDefaultSettings = computed(
  () =>
    defaultSharing !== undefined &&
    defaultSharing.visibility !== SHARING_TYPES.HIDDEN,
);

const selectedSingleSharePreset = computed(() =>
  singleSharePresets.find((preset) => preset.id === selectedPresetId.value),
);

const presetOptionsWithAction = computed(() => [
  ...(presetOptions ?? []),
  {
    heading: null,
    items: [
      {
        value: selectedSingleSharePreset.value?.id ?? 'custom',
        label: isForPartner.value
          ? `Custom field selection${selectedSingleSharePreset.value ? '' : '...'}`
          : 'Create Fields Preset',
        icon: (selectedSingleSharePreset.value
          ? undefined
          : ['far', 'plus']) as [BittsIconPrefix, string],
      },
    ],
  },
]);

async function handleChange(changes: {
  visibility?: SharingType;
  presetId?: number | 'custom';
}) {
  // View/edit existing preset
  if (isOpenFieldPresetsModal.value) return;

  if (changes.presetId === 'custom') {
    handleOpenCreatePresetModal();
    return;
  }

  // Select existing preset
  const createFromDefault = isForPartner.value && dataShare.partnerId === null;
  const changeFn = createFromDefault ? createDataShare : updateDataShare;
  await changeFn({
    populationId: dataShare.populationId,
    visibility: changes.visibility ?? dataShare.visibility,
    presetId: changes.presetId ?? dataShare.presetId,
  });
}

// Triggered when preset is created/edited and its data being passed as query in the router.
watch(
  () => [route?.query.newPresetId, route?.query.presetId],
  ([newPresetId, presetId]) => {
    const id = newPresetId || presetId;
    if (id && Number(route.query.populationId) === dataShare.populationId) {
      handleChange({ presetId: Number(id) });
      const newQuery = { ...router.currentRoute.value.query };
      delete newQuery.newPresetId;
      delete newQuery.presetId;
      delete newQuery.populationId;
      router.replace({ query: newQuery });
    }
    isOpenFieldPresetsModal.value = false;
  },
);

// Helpers / statics

// Check if a route name exists in matched routes
function isRouteNameInMatched(routeName: string) {
  return route.matched?.map((r) => r.name).includes(routeName) || false;
}

const sourceIdParam = isGoogleOrCsvSource(String(dataShare.integrationType))
  ? dataShare.sourceId
  : undefined;

const query = { ...route.query, populationId: dataShare.populationId };

function handleOpenPresetModal(event: Event, presetId: number) {
  isOpenFieldPresetsModal.value = true;

  // Ensures dropdown removal without selecting related preset
  selectRef.value?.instantBlur();
  event.stopPropagation();
  event.preventDefault();

  router.push({
    name: getRouteName('preset-details'),
    params: {
      feed_id: dataShare.feedId,
      source_id: sourceIdParam,
      preset_id: presetId,
      ...(isFromDefaultSharing.value
        ? {}
        : {
            partner_org_id: dataShare.partnerId || route.params.partner_org_id,
          }),
    },
    query,
  });
}

function handleOpenCreatePresetModal() {
  router.push({
    name: getRouteName('create-preset'),
    params: {
      feed_id: dataShare.feedId,
      source_id: sourceIdParam,
    },
    query,
  });
}

const { hasFeatureFlag } = useFeatureFlagStore();
const hasSharingControls = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_SHARING_CONTROLS),
);

onBeforeMount(async () => {
  if (!isSupernodeTier.value || !hasSharingControls.value || isAdmin.value)
    return;
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/organization/settings/sharing-controls',
  );
  if (error) throw error;
  isGreenfieldSettingEnabled.value = data.is_greenfield_setting_enabled;
});

async function handleRestoreToDefault() {
  await resetDataShare(dataShare);
}

function getPresetSuffixIcon(presetId?: number) {
  if (!presetId) return null;

  const crossbeamRecommendedPresetOption = presetOptionsWithAction.value
    ?.flatMap((group) => group.items)
    .find((item) => item.label === 'Recommended Fields Preset');

  const isCrossbeamRecommendedPreset =
    crossbeamRecommendedPresetOption?.value === presetId;

  return [
    'far',
    !disabled.value &&
    ((!isForPartner.value && hasPopulationsPermission) ||
      presetId === selectedSingleSharePreset.value?.id) &&
    !isCrossbeamRecommendedPreset
      ? 'pen'
      : 'eye',
  ] as [BittsIconPrefix, string];
}

function getRouteName(suffix: string) {
  if (isFromInviteOnboarding.value) {
    return `onboarding-invite-sharing-${suffix}`;
  }
  if (isFromSharingOnboarding.value) {
    return `onboarding-sharing-${suffix}`;
  }
  if (isFromInvitePartners.value) {
    return `partners-invite-sharing-${suffix}`;
  }
  if (isFromPendingRequests.value) {
    return `pending-requests-invite-sharing-${suffix}`;
  }
  if (isFromDefaultSharing.value) {
    return `default-sharing-${suffix}`;
  }
  return `sharing-${suffix}`;
}
</script>

<style lang="pcss" scoped>
.c-sharing-settings-item {
  @apply flex justify-between p-16;
}
</style>

<style lang="pcss">
.c-sharing-settings-item__sharing {
  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply h-24 border-none rounded-[28px] shadow-none bg-info-background-weak;
  }
}
.c-sharing-settings-item__sharing--overlap-counts {
  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply bg-warning-background-weak;
  }
}
.c-sharing-settings-item__sharing--dropdown {
  @apply overflow-x-hidden;
  .ant-select-item {
    @apply w-[300px] p-10 flex items-center;
  }
}
.c-sharing-settings-item__sharing-option-description {
  @apply text-neutral-text-weak text-sm text-wrap;
}
.ant-select-selection-item {
  .c-sharing-settings-item__sharing-option-description,
  .c-sharing-settings-item__preset-details-btn {
    @apply hidden;
  }
}
.c-sharing-settings-item__preset-dropdown {
  @apply min-w-[400px] !important;

  & .rc-virtual-list-holder {
    @apply max-h-[240px] !important;
  }

  .ant-select-item .ant-select-item-option-content {
    @apply ml-24;
    svg.fa-plus {
      @apply ml-[-24px] absolute;
    }
  }
  .ant-select-item-option-selected .ant-select-item-option-state {
    @apply absolute;
  }

  .c-bitts-btn--ghost {
    @apply bg-white;
  }
}

.ant-select-item-option-content {
  .c-sharing-settings-item__sharing-option-label {
    @apply font-bold text-neutral-text-strong !important;
  }
}
.c-sharing-settings-item__preset {
  @apply max-w-[500px];

  &.bitts-select
    .ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    @apply h-24 border-none rounded-[28px] shadow-none;
  }

  &.bitts-select .ant-select-focused .ant-select-selector {
    @apply border-none shadow-none !important;
  }

  &.bitts-select .ant-select-selection-placeholder {
    @apply flex items-center;
  }
}
.c-sharing-settings-item__partner-btn {
  &.c-bitts-btn-small {
    @apply p-4;
  }
}
</style>
